import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { BackfillCategoryStep } from '@/modules/benefits/components/BackfillCategoryStep'
import { BackfillRulesStep } from '@/modules/benefits/components/BackfillRulesStep'
import { BackfillPreviewStep } from '@/modules/benefits/components/BackfillPreviewStep'
import { BackfillExecuteStep } from '@/modules/benefits/components/BackfillExecuteStep'
import { BackfillSummaryStep } from '@/modules/benefits/components/BackfillSummaryStep'
import { useBackfillPreview } from '@/modules/benefits/hooks/useBackfillPreview'
import { useBackfillGrant } from '@/modules/benefits/hooks/useBackfillGrant'
import { CATEGORY_DISPLAY_NAMES } from '@/modules/benefits/types'

type Step = 'category' | 'rules' | 'preview' | 'execute' | 'summary'

const STEP_LABELS: Record<Step, string> = {
  category: 'Seleccionar nivel',
  rules: 'Seleccionar reglas',
  preview: 'Preview',
  execute: 'Ejecutar',
  summary: 'Resumen',
}

const STEPS: Step[] = ['category', 'rules', 'preview', 'execute', 'summary']

export function BenefitsBackfillPage() {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState<Step>('category')
  const [selectedCategory, setSelectedCategory] = useState<string>('')
  const [selectedRuleIds, setSelectedRuleIds] = useState<number[]>([])
  const [backfillReason, setBackfillReason] = useState('')

  const {
    preview,
    isLoading: isPreviewLoading,
    error: previewError,
    fetchPreview,
    reset: resetPreview,
  } = useBackfillPreview()

  const {
    isRunning,
    isPaused,
    progress,
    total,
    logs,
    summary,
    execute,
    pause,
    resume,
    cancel,
  } = useBackfillGrant()

  const handleCategorySelect = useCallback((category: string) => {
    setSelectedCategory(category)
    setCurrentStep('rules')
  }, [])

  const handleRulesConfirm = useCallback(
    async (ruleIds: number[]) => {
      setSelectedRuleIds(ruleIds)
      setCurrentStep('preview')
      await fetchPreview(selectedCategory, ruleIds)
    },
    [selectedCategory, fetchPreview],
  )

  const handlePreviewConfirm = useCallback(
    async (reason: string) => {
      if (!preview) return
      setBackfillReason(reason)
      setCurrentStep('execute')
      await execute(preview.clients, selectedRuleIds, reason)
    },
    [preview, selectedRuleIds, execute],
  )

  const handleBack = useCallback(
    (toStep: Step) => {
      resetPreview()
      setCurrentStep(toStep)
    },
    [resetPreview],
  )

  const handleFinish = useCallback(() => {
    navigate('/benefits')
  }, [navigate])

  // If execution is done (summary available), show summary
  const effectiveStep = summary && currentStep === 'execute' ? 'summary' : currentStep

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate('/benefits')}
          className="text-gray-400 hover:text-gray-600"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Asignación retroactiva de beneficios</h1>
          {selectedCategory && (
            <p className="text-sm text-gray-500 mt-0.5">
              Nivel: {CATEGORY_DISPLAY_NAMES[selectedCategory]}
              {selectedRuleIds.length > 0 && ` — ${selectedRuleIds.length} regla${selectedRuleIds.length !== 1 ? 's' : ''}`}
              {backfillReason && ` — ${backfillReason}`}
            </p>
          )}
        </div>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-8">
        {STEPS.map((step, i) => {
          const isActive = i === STEPS.indexOf(effectiveStep)
          const isCompleted = i < STEPS.indexOf(effectiveStep)
          return (
            <div key={step} className="flex items-center gap-2">
              {i > 0 && (
                <div className={`w-8 h-0.5 ${isCompleted ? 'bg-primary-500' : 'bg-gray-200'}`} />
              )}
              <div className="flex items-center gap-1.5">
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
                    isActive
                      ? 'bg-primary-600 text-white'
                      : isCompleted
                        ? 'bg-primary-100 text-primary-700'
                        : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  {isCompleted ? (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    i + 1
                  )}
                </div>
                <span
                  className={`text-xs font-medium hidden sm:inline ${
                    isActive ? 'text-primary-700' : isCompleted ? 'text-primary-600' : 'text-gray-400'
                  }`}
                >
                  {STEP_LABELS[step]}
                </span>
              </div>
            </div>
          )
        })}
      </div>

      {/* Step content */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        {effectiveStep === 'category' && (
          <BackfillCategoryStep onSelect={handleCategorySelect} />
        )}

        {effectiveStep === 'rules' && (
          <BackfillRulesStep
            category={selectedCategory}
            onConfirm={handleRulesConfirm}
            onBack={() => handleBack('category')}
          />
        )}

        {effectiveStep === 'preview' && preview && (
          <BackfillPreviewStep
            preview={preview}
            isLoading={isPreviewLoading}
            error={previewError}
            onConfirm={handlePreviewConfirm}
            onBack={() => handleBack('rules')}
          />
        )}

        {effectiveStep === 'preview' && !preview && isPreviewLoading && (
          <div className="space-y-4">
            <div className="animate-pulse h-32 bg-gray-100 rounded-lg" />
            <div className="animate-pulse h-48 bg-gray-100 rounded-lg" />
          </div>
        )}

        {effectiveStep === 'preview' && !preview && previewError && (
          <div className="space-y-4">
            <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
              {previewError}
            </div>
            <button onClick={() => handleBack('rules')} className="btn btn-secondary">
              Volver
            </button>
          </div>
        )}

        {effectiveStep === 'execute' && (
          <BackfillExecuteStep
            isRunning={isRunning}
            isPaused={isPaused}
            progress={progress}
            total={total}
            logs={logs}
            onPause={pause}
            onResume={resume}
            onCancel={cancel}
          />
        )}

        {effectiveStep === 'summary' && summary && (
          <BackfillSummaryStep
            summary={summary}
            category={selectedCategory}
            onFinish={handleFinish}
          />
        )}
      </div>
    </div>
  )
}
